import { Box, Typography } from '@material-ui/core';
import { ColorDynamic, Inline, Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEvent, chooseUTMSource } from 'shared/helpers/Analytics';
import { AddLoadAlertIcon } from 'shared/icons/AddLoadAlertIcon';
import { NudgeNextStepIcon } from 'shared/icons/NudgeNextStepIcon';
import { NudgeSentIcon } from 'shared/icons/NudgeSentIcon';
import { createSearchCriteria } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { updateSearchQuery } from 'shared/utils/URLUtils';
import styled from 'styled-components';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import {
  dismissLoadAlertNudgingDialog,
  formatRadius,
  useLoadAlertNudge,
  useSetLoadAlertNudge,
} from '../../core/nudging/PostingDetailsNudgeHelper';
import nudgeImg from '../../core/nudging/assets/load_alert_nudging_img.png';
import { PostingWaypoints } from '../../core/postings/PostingWaypoints';
import { usePostingsPageContext } from '../../core/postings/PostingsPageContext';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';
import {
  LoadSearchSource,
  convertPostingPageParamsToQuery,
} from '../../data/dto/PostingsPageParamsDTO';
import { LoadAlertDialog } from '../LoadAlertDialog';
import { PostingDetailsModal } from './PostingDetailsModal';

const Container = styled.div`
  overflow-x: hidden;
  padding: 16px 0 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background: ${ColorDynamic.White};
  height: 100%;
`;

const ButtonWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  row-gap: 16px;
  padding: 16px;
  column-gap: 16px;
`;

const NudgeLoadAlertButton = styled(Button)`
  padding: 16px;
`;

interface PostingDetailsLoadAlertNudgeMobileProps {
  isBook: boolean;
  load: PostingLoadDTO;
  onClose: () => void;
}

function PostingDetailsLoadAlertNudgeMobile({
  isBook,
  onClose,
  load,
}: PostingDetailsLoadAlertNudgeMobileProps) {
  useEffect(() => {
    trackEvent('SLB: Viewed Load Alert Nudging Dialog', {
      utm_medium: 'Load Details',
    });
  }, []);

  const { params } = usePostingsPageContext();
  const {
    router: { closePostingScreen },
  } = useLoadboardContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [dialogState, setDialogState] = useState<{
    source: LoadSearchSource;
  }>();

  const radius = useMemo(() => {
    return {
      pickup: formatRadius(params.search_criteria.pickup_radius),
      delivery: formatRadius(params.search_criteria.delivery_radius),
    };
  }, [params]);

  const venues = useMemo(() => {
    return createSearchCriteria({
      pickup_venues: [load.pickup.venue],
      delivery_venues: [load.delivery.venue],
    });
  }, [load]);

  const openFilterPage = useCallback(
    (source: LoadSearchSource, create?: boolean) => {
      navigate({
        pathname: '/filter',
        search: updateSearchQuery(location.search, () =>
          convertPostingPageParamsToQuery({
            utm_medium: 'Load Alert Nudging Dialog',
            search_criteria: venues,
            load_alert: 'create',
          }),
        ),
      });
      if (create) {
        trackLoadboardEvent({ name: 'SLB: Clicked Create Load Alert', source });
      }
    },
    [navigate, location.search, venues],
  );

  const handleSavedSearchCreate = useCallback(
    (source: LoadSearchSource) => {
      setDialogState({ source });
      trackLoadboardEvent({ name: 'SLB: Clicked Create Load Alert', source });
    },
    [setDialogState],
  );

  return (
    <Container>
      <Box display="flex" maxWidth="360px">
        <Stack space="small" align="center">
          <img width="328px" loading="lazy" src={nudgeImg} alt="" />
          <Stack space="small">
            <Inline verticalAlign="center">
              <NudgeSentIcon fontSize="large" />
              <Typography variant="h3" color="textSecondary">
                {isBook ? 'Load booked' : 'Request sent'}
              </Typography>
            </Inline>

            <Stack space="xsmall">
              <Inline verticalAlign="top" noWrap={true}>
                <NudgeNextStepIcon fontSize="large" />
                <Typography variant="h3">
                  Next, be the first to book new <br /> loads posted on this
                  route
                </Typography>
              </Inline>
              <Box
                paddingLeft="40px"
                display="flex"
                flexDirection="column"
                gridGap="8px"
              >
                <Typography variant="body1" color="textSecondary">
                  Get Free SMS & Email notifications for:
                </Typography>

                <PostingWaypoints load={load} radius={radius} />
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <ButtonWrapper>
        <Button
          variant="text"
          size="large"
          fullWidth={true}
          onClick={() => {
            dismissLoadAlertNudgingDialog();
            trackEvent('SLB: Dismissed Load Alert Nudging', {
              utm_source: chooseUTMSource(),
              utm_medium: 'Load Alert Nudging Dialog',
            });
            onClose();
          }}
        >
          Dismiss
        </Button>

        <NudgeLoadAlertButton
          startIcon={<AddLoadAlertIcon />}
          variant="primary"
          size="large"
          fullWidth={true}
          onClick={() => {
            handleSavedSearchCreate('Load Alert Nudging Dialog');
          }}
        >
          Create Load Alert - It’s Free
        </NudgeLoadAlertButton>
      </ButtonWrapper>

      <LoadAlertDialog
        open={!!dialogState}
        source={dialogState?.source}
        onClose={() => {
          setDialogState(undefined);
        }}
        searchCriteria={venues}
        onCustomizeClick={() => {
          if (dialogState) {
            openFilterPage(dialogState.source, true);
          }
        }}
        onSuccess={() => {
          setDialogState(undefined);
          closePostingScreen();
        }}
      />
    </Container>
  );
}

export function PostingDetailsLoadAlertNudgeDialog() {
  const postingNudge = useLoadAlertNudge();
  const setPostingNudge = useSetLoadAlertNudge();

  if (!postingNudge) {
    return null;
  }

  return (
    <PostingDetailsModal>
      <PostingDetailsLoadAlertNudgeMobile
        isBook={postingNudge.isBook}
        load={postingNudge.load}
        onClose={() => {
          setPostingNudge(undefined);
        }}
      />
    </PostingDetailsModal>
  );
}
